import classNames from 'classnames';
import * as React from 'react';
import { useTheme } from '~/Features/Layouts/Theme/Context/ThemeContextProvider';
import { MessageBroker } from '~/Features/Messaging/MessageBroker';
import { useClickOutsideRefObserver } from '~/Utils/Hooks/useClickOutsideObserver';
import type { Theme } from '~/Utils/Theme';
import { NavigationApplyClick } from './Events/NavigationApplyClick';
import styles from './Navigation.module.css';
import { NavigationControls } from './NavigationControls/NavigationControls';
import { NavigationList } from './NavigationList/NavigationList';
import type { NavigationAnchor } from './Utils/types';
import { useCurrentlyVisibleSection } from './Utils/useCurrentlyVisibleSection';

interface NavigationProps {
    sectionAnchors: NavigationAnchor[];
    translations: {
        apply: string;
        label: string;
        home: string;
    };
    theme?: Theme;
    className?: string;
}

export const Navigation: React.FC<NavigationProps> = props => {
    const contextualTheme = useTheme();
    const { sectionAnchors, translations, className, theme = contextualTheme } = props;
    const { withBoxShadow, withRoundedCorners } = theme;

    const [isNavigationExpanded, setIsNavigationExpanded] = React.useState(false);
    const navigationRef = React.useRef<HTMLDivElement>(null);

    const { currentVisibleSectionId } = useCurrentlyVisibleSection(sectionAnchors);

    const currentItem = sectionAnchors.find(anchor => anchor.id === currentVisibleSectionId);

    const navigationId = 'study-page-navigation';

    const handleNavigationExpansion = () => {
        setIsNavigationExpanded(!isNavigationExpanded);
    };

    const closeNavigation = () => {
        setIsNavigationExpanded(false);
    };

    const onApplyClick = () => {
        MessageBroker.publish(new NavigationApplyClick());
    };

    useClickOutsideRefObserver(navigationRef, closeNavigation);

    return (
        <div
            ref={navigationRef}
            className={classNames(
                styles.Navigation,
                withRoundedCorners && styles['Variant--rounded'],
                withBoxShadow && styles['Variant--shadowed'],
                className
            )}
        >
            <div className={styles.Inner}>
                <NavigationControls
                    isOpen={isNavigationExpanded}
                    applyButtonLabel={translations.apply}
                    onApplyClick={onApplyClick}
                    onExpandClick={handleNavigationExpansion}
                    currentItemLabel={currentItem?.label}
                    ariaControlsId={navigationId}
                    theme={theme}
                >
                    <NavigationList
                        isOpen={isNavigationExpanded}
                        sectionAnchors={[{ id: 'study-page-hero', label: translations.home }, ...sectionAnchors]}
                        currentItem={currentItem}
                        onItemClick={closeNavigation}
                        ariaControlsId={navigationId}
                        label={translations.label}
                        theme={theme}
                    />
                </NavigationControls>
            </div>
        </div>
    );
};
